import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import Modal from 'react-modal'
import { Box, IconButton, Button, TooltipComponent } from '../../../components'
import { useTheme } from '../../../index'
import { useNotificationsHomeContext } from '../../context'
import MemoClose from '../Icons/Close'
import isEqual from 'lodash/isEqual'

import { PanelBody, PreferenceSaveButton } from '../ConfigComponents'

// Set app element for accessibility
if (typeof document !== 'undefined') {
  Modal.setAppElement(document.body)
}

const DialogTitle = React.memo(({ handleClose, titleRef }) => {
  const theme = useTheme()

  const shadowColor =
    theme.palette.mode === 'dark'
      ? 'rgb(125 104 163 / 13%)'
      : 'rgba(58, 53, 65, 0.10)'

  const handleCloseClick = useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      handleClose()
    },
    [handleClose]
  )

  return (
    <div
      style={{
        display: 'flex',
        alignITems: 'center',
        justifyContent: 'space-between',
        boxShadow: `0px 2px 10px 0px ${shadowColor}`,
        padding: '0 1rem',
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary
      }}
    >
      <h2
        ref={titleRef}
        tabIndex={-1}
        style={{
          fontSize: '16px',
          fontWeight: 500,
          margin: '1rem 0',
          outline: 'none'
        }}
      >
        Notification Preferences
      </h2>

      <IconButton onClick={handleCloseClick}>
        <MemoClose color={theme.palette.text.secondary} />
      </IconButton>
    </div>
  )
})
DialogTitle.displayName = 'DialogTitle'

const dialogActionsStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  alignSelf: 'center',
  padding: '4px'
}

const DialogActions = React.memo(({ children }) => {
  return <div style={dialogActionsStyle}>{children}</div>
})
DialogActions.displayName = 'DialogActions'

export const ConfigPopup = ({ preferenceMode }) => {
  const theme = useTheme()
  const titleRef = useRef()

  const {
    data: {
      userPreference,
      resetPreference,
      resetGlobalChannelPreference,
      globalChannelPreference
    },
    handlers: { setShowConfig, setOpenConfigUnsaved }
  } = useNotificationsHomeContext()

  const isOpen = preferenceMode === 'modal'

  const hasUnsavedChanges = useMemo(() => {
    return (
      !isEqual(userPreference, resetPreference) ||
      !isEqual(globalChannelPreference, resetGlobalChannelPreference)
    )
  }, [
    userPreference,
    resetPreference,
    globalChannelPreference,
    resetGlobalChannelPreference
  ])

  const handleCloseDialog = useCallback(() => {
    if (hasUnsavedChanges) {
      setOpenConfigUnsaved(true)
    } else {
      setShowConfig(false)
    }
  }, [hasUnsavedChanges, setOpenConfigUnsaved, setShowConfig])

  // Handle ESC key manually to have full control
  useEffect(() => {
    if (!isOpen) {
      return
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        handleCloseDialog()
      }
    }

    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [isOpen, handleCloseDialog])

  const modalStyles = useMemo(
    () => ({
      overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(3px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999999
      },
      content: {
        position: 'relative',
        top: 'auto',
        left: 'auto',
        right: 'auto',
        bottom: 'auto',
        padding: '0',
        border: 'none',
        borderRadius: '12px',
        boxShadow:
          '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        width: 'min(600px, 90vw)',
        maxHeight: '80vh',
        overflow: 'auto',
        backgroundColor: theme.palette.background.paper || '#ffffff',
        inset: 'auto'
      }
    }),
    [theme.palette.background.paper]
  )

  return (
    <Modal
      isOpen={isOpen}
      // onAfterClose={handleAfterClose}
      onRequestClose={() => {}}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={false}
      shouldCloseOnBlur={false}
      shouldFocusAfterRender={false}
      shouldReturnFocusAfterClose={false}
      ariaHideApp={false}
      style={modalStyles}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ overflow: 'hidden' }}>
        <DialogTitle handleClose={handleCloseDialog} titleRef={titleRef} />

        <PanelBody />

        <DialogActions sx={{ alignSelf: 'center' }}>
          {hasUnsavedChanges ? (
            <PreferenceSaveButton variant='contained' />
          ) : (
            <TooltipComponent title='No changes to save'>
              <Box sx={{ m: 1, position: 'relative', flexGrow: '1' }}>
                <Button variant='contained' size='small'>
                  Save
                </Button>
              </Box>
            </TooltipComponent>
          )}
        </DialogActions>
      </div>
    </Modal>
  )
}

export default ConfigPopup
